import { useFetchPage } from "~/core/composables/useFetchPage";
import { SearchMode, useSearch } from "~/core/composables/useSearch";
import { useSort } from "~/core/composables/useSort";
import type LxcError from "~/core/models/LxcError.class";
import type { UseFetchParameters } from "~/core/models/UseFetchParameters.type";
import type { UseFetchResponse } from "~/core/models/UseFetchResponse.interface";
import { FiltersType } from "~/core/models/filters";
import { fetchAllPaginatedList } from "~/core/utils/fetch";
import type { MobileInterface } from "~/modules/applicationManagement/models/Mobile.interface";
import mobileInterfaceService from "~/modules/applicationManagement/services/mobileInterface.service";

interface UseFetchMobileInterfacesResponse extends UseFetchResponse<MobileInterface> {
  fetchAllMobileInterfaces: () => Promise<LxcError | MobileInterface[]>;
}

export function useFetchMobileInterfaces({
  organizationCode,
}: UseFetchParameters): UseFetchMobileInterfacesResponse {
  const service = mobileInterfaceService.findWithPagination.bind(
    mobileInterfaceService,
  );

  const { searchParams, filters, setFilter, search, onSearch } = useSearch(
    FiltersType.PIPE_SEPARATOR,
    new Map(),
    SearchMode.URL_SEARCH,
  );
  const { sort, onSortChange } = useSort();
  const { isLoading, results, error, fetchData } = useFetchPage({
    organizationCode,
    service,
    searchParams,
    sort,
    useQueryParametersForPagination: true,
  });

  async function fetchAllMobileInterfaces(): Promise<
    MobileInterface[] | LxcError
  > {
    return await fetchAllPaginatedList((page: number, pageSize: number) =>
      mobileInterfaceService.findWithPagination({
        organizationCode,
        page,
        pageSize,
        searchParams: "",
      }),
    );
  }

  return {
    isLoading,
    results,
    error,
    filters,
    fetchData,
    setFilter,
    onSortChange,
    search,
    onSearch,
    fetchAllMobileInterfaces,
  };
}
